import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';

import {
  ChildSurveyFilter,
  GetSurveysParams,
  SurveySummary,
  Survey,
  SurveyChannelCreationConfirmedEvent,
  SurveyHealthMetric,
  FindSurveysParams,
  SurveySearchParams,
  SurveyQuotaUpdate,
  SurveyStatusCount,
  Targeting,
  TemplateDetail,
} from '@ps/survey-utils';
import {
  SurveyStatus,
  type HighCpiConfirmationDetails,
} from '@ps/survey-kernel';
import { isTimeoutError, PSError } from '@ps/utils';

import {
  RESPONDENT_LIST_TEMPLATE_FILE_NAME,
  RESPONDENT_LIST_TEMPLATE_PATH,
  SURVEY_TIMEOUT_MESSAGE,
} from '../../utils';
import { QuotaPage, SurveyApi } from '../../api/survey.api';
import { QUOTAS_PAGE_LIMIT } from '../../state/survey-state.constants';
import { SurveyPatchPayloadDto } from '../../api/dtos/survey.dto';
import { LaunchTrafficChannelPayloadMapper } from '../../api/mapper/launch-traffic-channel.payload.mapper';
import { BulkLaunchTcReplicaPayloadMapper } from '../../api/mapper/bulk-edit-payload.mapper';
import { BillingRuleRecord } from '@ps/user-settings-models';
import { LoadingScope } from '@ps/core-loading';
import { SurveyMapper } from '../../api/mapper/survey.mapper';
import { StandardSurveyPayloadInput } from '../../api/mapper/standard-survey.payload.mapper';

@Injectable({
  providedIn: 'root',
})
export class SurveyService {
  private readonly _api = inject(SurveyApi);
  private readonly _document = inject(DOCUMENT);
  private readonly _launchTrafficChannelPayloadMapper =
    new LaunchTrafficChannelPayloadMapper();
  private readonly _bulkLaunchTcReplicaPayloadMapper =
    new BulkLaunchTcReplicaPayloadMapper();
  private readonly _mapper = new SurveyMapper();

  public getAll(params: GetSurveysParams): Observable<Survey[]> {
    return this._api.getAll(params).pipe(
      catchError((error) => {
        if (isTimeoutError(error)) {
          return throwError(() =>
            PSError.from(SURVEY_TIMEOUT_MESSAGE, 'survey-load-timeout', {
              kind: 'timeout',
              notify: true,
            }),
          );
        }
        return throwError(() => error);
      }),
    );
  }

  public getCounts(params: GetSurveysParams): Observable<SurveyStatusCount> {
    return this._api.getCounts(params);
  }

  public getChildSurveys(
    parentId: number,
    filter: ChildSurveyFilter,
  ): Observable<Survey[]> {
    return this._api.getChildSurveys(filter).pipe(
      // The grouped/child survey endpoint can return the parent row in the same list.
      // The expanded row is already shown above; drop it so the child list is only descendants.
      map((surveys) => surveys.filter((s) => s.id !== parentId)),
    );
  }

  public getTrafficChannelChildren(surveyId: number): Observable<Survey[]> {
    return this._api.getTrafficChannelChildren(surveyId);
  }

  public updateSurveyStatus(
    surveyId: number,
    status: SurveyStatus,
    scope: LoadingScope = 'drawer',
  ): Observable<void> {
    return this._api.updateSurveyStatus(surveyId, status, scope);
  }

  public getById(
    surveyId: number,
    targeting: Targeting = 0,
    isChild = false,
    scope: LoadingScope = 'drawer',
  ): Observable<Survey> {
    return this._api.getById({
      id: surveyId,
      targeting,
      accessible: isChild,
      scope,
    });
  }

  public getByIdTargeting(
    surveyId: number,
    isChild = false,
  ): Observable<Survey> {
    return this._api.getById({
      id: surveyId,
      targeting: 1,
      accessible: isChild,
      scope: 'page',
    });
  }

  public getHighCpiDetails(
    surveyId: number,
  ): Observable<HighCpiConfirmationDetails> {
    return this._api.getHighCpiDetails(surveyId);
  }

  public getHealth(surveyId: number): Observable<SurveyHealthMetric[]> {
    return this._api.getHealth(surveyId);
  }

  public updateCurrentCpi(
    surveyId: number,
    cpi: number,
    isChild = false,
    scope: LoadingScope = 'drawer',
  ): Observable<Survey> {
    const targeting = 0;
    return this._api
      .updateCurrentCpi(surveyId, cpi, scope)
      .pipe(
        switchMap(() =>
          this._api.getById({ id: surveyId, targeting, accessible: isChild }),
        ),
      );
  }

  public patchSurvey(
    surveyId: number,
    payload: SurveyPatchPayloadDto[],
    scope: LoadingScope = 'drawer',
  ): Observable<void> {
    return this._api.patchSurvey(surveyId, payload, scope);
  }

  public getQuotas(surveyId: number, page?: number): Observable<QuotaPage> {
    const limit = page ? QUOTAS_PAGE_LIMIT : undefined;
    return this._api.getQuotas(surveyId, page, limit);
  }

  public setQuotaLock(
    surveyId: number,
    psQuotaId: string,
    locked: boolean,
  ): Observable<void> {
    return this._api.setQuotaLock(surveyId, psQuotaId, locked);
  }

  public setQuotaCurrentTarget(
    surveyId: number,
    psQuotaId: string,
    currentTarget: number,
    updateSupplier: boolean,
  ): Observable<SurveyQuotaUpdate> {
    return this._api.setQuotaCurrentTarget(
      surveyId,
      psQuotaId,
      currentTarget,
      updateSupplier,
    );
  }

  public launchTrafficChannel(
    surveyId: number,
    event: SurveyChannelCreationConfirmedEvent,
  ): Observable<Survey[]> {
    return this._api.launchTrafficChannel(
      surveyId,
      this._launchTrafficChannelPayloadMapper.toPayload(event),
      false,
    );
  }

  public launchTcReplicaBulk(surveyId: number): Observable<Survey[]> {
    return this._api.launchTrafficChannel(
      surveyId,
      this._bulkLaunchTcReplicaPayloadMapper.toPayload(),
      true,
    );
  }

  /**
   * Combined multi-criteria lookup (Advanced Filters' selected criteria + date
   * range) — never carries a free-text search term, unlike `searchSurveySummaries`.
   *
   */
  public getSurveySummaries(
    params: FindSurveysParams,
  ): Observable<SurveySummary[]> {
    const { criteria, dateRange } = params;

    const hasSomethingToLookUp =
      criteria.some(({ values }) => values.length > 0) || !!dateRange;

    if (!hasSomethingToLookUp) return of([]);

    return this._api.getSurveySummaries(params);
  }

  /**
   * Named separately from `getSurveySummaries` for call-site clarity in the
   * search-widget/typeahead contexts (Billing Number, Survey Title, Groups).
   */
  public searchSurveySummaries(
    params: SurveySearchParams,
  ): Observable<SurveySummary[]> {
    return this._api.searchSurveySummaries(params);
  }

  public downloadRespondentListTemplate(): Observable<void> {
    const anchor = this._document.createElement('a');
    anchor.href = RESPONDENT_LIST_TEMPLATE_PATH;
    anchor.download = RESPONDENT_LIST_TEMPLATE_FILE_NAME;
    anchor.rel = 'noopener noreferrer';
    this._document.body.appendChild(anchor);
    anchor.click();
    this._document.body.removeChild(anchor);

    return of(undefined);
  }

  public searchInvoicedSurveysWithBillingId(
    billingId: string,
  ): Observable<void> {
    return this._api.searchInvoicedSurveysWithBillingId(billingId);
  }

  public verifyBillingIdAgainstBillingRules(
    billingId: string,
    billingRuleRecord: BillingRuleRecord,
  ): Observable<void> {
    return this._api.verifyBillingIdAgainstBillingRules(
      billingId,
      billingRuleRecord,
    );
  }

  public groupSurveys(
    surveyIds: number[],
    groupTitle: string,
  ): Observable<void> {
    return this._api.groupSurveys(surveyIds, groupTitle);
  }

  public deleteSurvey(surveyId: number): Observable<void> {
    return this._api.deleteSurvey(surveyId);
  }

  public createSurveyFromTemplate(
    detail: TemplateDetail,
    userId: number,
    companyId: number,
  ): Observable<Survey> {
    return this._api
      .createSurveyFromTemplate(detail, userId, companyId)
      .pipe(map((dto) => this._mapper.fromDetailsDto(dto)));
  }

  public createStandardSurvey(
    input: StandardSurveyPayloadInput,
  ): Observable<Survey> {
    return this._api
      .createStandardSurvey(input)
      .pipe(map((dto) => this._mapper.fromDetailsDto(dto)));
  }

  public updateStandardSurvey(
    surveyId: number,
    input: StandardSurveyPayloadInput,
  ): Observable<Survey> {
    return this._api
      .updateStandardSurvey(surveyId, input)
      .pipe(map((dto) => this._mapper.fromDetailsDto(dto)));
  }

  public cloneStandardSurvey(
    surveyId: number,
    isServiceOperatorCreation: boolean,
  ): Observable<Survey> {
    return this._api
      .cloneStandardSurvey(surveyId, isServiceOperatorCreation)
      .pipe(map((dto) => this._mapper.fromDetailsDto(dto)));
  }
}
